/**
 * Phase 49, Signup gated for Private Beta.
 *
 * During the private beta phase, free-form signups (email + password OR
 * Google) are disabled. Invited users get a one-time link in their email
 * that lands them on `/accept-invite` instead.
 *
 * This page now does three calm things:
 *   1. Explains the invitation-only state.
 *   2. Sends would-be visitors to 2120.money to join the waitlist.
 *   3. Points returning users to /login.
 *
 * When you re-open public signup, revert to the prior version of this file
 * from git history, the AuthContext.signUp method and the trigger guard
 * are still in place server-side, so it'll work without re-plumbing.
 */
import React, { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Sparkles, Shield, Lock, MailCheck, Globe2, Heart } from 'lucide-react';
import usePageTitle from '@/hooks/usePageTitle';

export default function Signup() {
  usePageTitle('2120, private beta');
  const [searchParams] = useSearchParams();

  // Phase 71 — capture a referral token from `?ref=<token>` and persist
  // it in sessionStorage. The AppShell will pick it up the moment the
  // visitor actually signs up + lands inside the app, and call
  // /api/invitations/resolve once. Storage is scoped to this tab and
  // expires the moment the browser closes — calm by default.
  const refToken = (searchParams.get('ref') || '').trim();
  useEffect(() => {
    if (!refToken) return;
    try {
      window.sessionStorage.setItem('_2120_pending_ref', refToken);
    } catch { /* private mode / Safari ITP — silently ignore */ }
  }, [refToken]);

  // Phase 71 — forward the ref to the marketing-site CTA so the waitlist
  // form on 2120.money can resolve who invited the visitor.
  const waitlistUrl = refToken
    ? `https://2120.money?ref=${encodeURIComponent(refToken)}`
    : 'https://2120.money';

  return (
    <div className="min-h-screen bg-background text-foreground grid md:grid-cols-2">
      {/* Left hero panel, matches Login / AcceptBetaInvite rhythm */}
      <div className="hidden md:flex bg-hero-soft items-center justify-center p-12">
        <div className="max-w-md space-y-6">
          <div className="flex items-center gap-3">
            <img src="/brand/logo-mark.png" alt="2120" className="h-10 w-10" />
            <img src="/brand/wordmark-green.png" alt="2120" className="h-7 w-auto dark:hidden" />
            <img src="/brand/wordmark-gold.png" alt="2120" className="h-7 w-auto hidden dark:block" />
          </div>
          <div className="inline-flex items-center gap-2 text-xs px-3 py-1 rounded-full bg-card border border-border/60 text-muted-foreground">
            <Sparkles className="h-3.5 w-3.5" /> Built for multi-country lives
          </div>
          <h1 className="font-display text-4xl lg:text-5xl font-semibold tracking-tight">
            Your money, finally in one calm view.
          </h1>
          <p className="text-muted-foreground leading-relaxed">
            For people who hold accounts across borders, currencies, and banks. Not a budgeting app. Not an advice service. A quiet place to understand your money.
          </p>
          {/* Phase 70 — Trust-audit (B2): sharper positioning that
              answers the three objections sceptical users actually have
              ("why not Mint", "do you sell my data", "why pay") instead
              of the previous generic "private + secure + control" set.
              Each line is concrete and falsifiable. */}
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-2.5">
              <Globe2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <span><span className="font-medium text-foreground">Across borders, in one view.</span> Track INR, AED, USD, SGD &mdash; everywhere you hold money.</span>
            </li>
            <li className="flex items-start gap-2.5">
              <Shield className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <span><span className="font-medium text-foreground">We work for you, not advertisers.</span> No ads, no data resale, ever.</span>
            </li>
            <li className="flex items-start gap-2.5">
              <Heart className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <span><span className="font-medium text-foreground">You&rsquo;re the customer, not the product.</span> A subscription keeps the incentives clean.</span>
            </li>
          </ul>
        </div>
      </div>

      {/* Right card */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <Card className="w-full max-w-md rounded-2xl border-border/70">
          <CardContent className="p-6 sm:p-8 space-y-6" data-testid="signup-private-beta-card">
            {/* Mobile-only brand header */}
            <div className="md:hidden flex flex-col items-center gap-2 pb-2 border-b border-border/50">
              <div className="flex items-center gap-2.5">
                <img src="/brand/logo-mark.png" alt="2120" className="h-9 w-9" />
                <img src="/brand/wordmark-green.png" alt="2120" className="h-6 w-auto dark:hidden" />
                <img src="/brand/wordmark-gold.png" alt="2120" className="h-6 w-auto hidden dark:block" />
              </div>
              <div className="text-xs text-muted-foreground text-center">Financial Clarity For Modern Life.</div>
            </div>

            <div className="space-y-2">
              <div className="inline-flex items-center gap-2 text-[11px] px-2.5 py-1 rounded-full bg-[hsl(var(--gold))]/12 text-[hsl(var(--gold))] border border-[hsl(var(--gold))]/30" data-testid="signup-beta-pill">
                <Lock className="h-3 w-3" /> Private beta · by invitation
              </div>
              <h1 className="font-display text-2xl font-semibold leading-tight">By invitation only, for now.</h1>
              <p className="text-sm text-muted-foreground">
                2120 is currently in a calm, limited private beta. We're opening access gradually so the experience stays considered for the people who join first.
              </p>
            </div>

            <div className="rounded-2xl border border-border/60 bg-muted/30 px-4 py-3.5 text-sm leading-relaxed text-foreground/85">
              <div className="flex items-start gap-2.5">
                <MailCheck className="h-4 w-4 mt-0.5 shrink-0 text-[hsl(var(--gold))]" />
                <div>
                  <div className="font-medium text-foreground mb-0.5">Already invited?</div>
                  <div className="text-muted-foreground">
                    Open the invitation email we sent you and click the button inside. It takes you to a private setup page where you can pick a password and step in.
                  </div>
                </div>
              </div>
            </div>

            <Button
              asChild
              className="w-full rounded-xl"
              data-testid="signup-join-waitlist-cta"
            >
              <a href={waitlistUrl} target="_blank" rel="noreferrer noopener">
                Join the waitlist at 2120.money
              </a>
            </Button>

            <div className="text-xs text-muted-foreground text-center leading-relaxed">
              You'll receive one quiet note when access is ready, no follow-ups, no upsells.
            </div>

            <div className="pt-3 border-t border-border/50 text-sm text-center text-muted-foreground">
              Already with us? <Link to="/login" className="text-primary hover:underline" data-testid="auth-go-login-link">Sign in</Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
